import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories, siteImages } from '../data/mock';
import { ChevronDown, LayoutGrid, Music2 } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('featured');

  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    document.title = 'Shop Musical Instruments & Music Gear | Sound Peak Gear';
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (categorySlug) => {
    if (categorySlug) setSearchParams({ category: categorySlug });
    else setSearchParams({});
  };

  const currentCategory = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-white text-dark-800">
      <Header />
      <CartDrawer />

      <main>
        {/* Banner header */}
        <section className="relative bg-dark-950 overflow-hidden">
          <img src={siteImages.shopBanner} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-950 via-dark-950/85 to-dark-950/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <nav className="text-sm text-dark-400 mb-3">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-white">{currentCategory ? currentCategory.name : 'Shop All'}</span>
            </nav>
            <h1 className="font-display text-3xl sm:text-5xl font-bold uppercase tracking-wide text-white">
              {currentCategory ? currentCategory.name : 'Shop All Instruments'}
            </h1>
            <p className="mt-3 text-dark-300 max-w-2xl">
              {currentCategory
                ? currentCategory.description
                : 'A tightly curated collection of instruments and music gear \u2014 strings, drums, woodwinds, studio kit and the accessories that keep you playing.'}
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-28">
                <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase tracking-wide text-dark-900 mb-4 pb-3 border-b-2 border-dark-900">
                  <Music2 className="w-4 h-4 text-athletic-600" /> Categories
                </h2>
                <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`text-left px-4 py-2.5 rounded-md text-sm font-semibold uppercase tracking-wide whitespace-nowrap transition-colors flex items-center justify-between ${!selectedCategory ? 'bg-athletic-500 text-white' : 'bg-dark-50 text-dark-700 hover:bg-dark-100'}`}
                  >
                    All Products <span className="text-xs opacity-80">{products.length}</span>
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`text-left px-4 py-2.5 rounded-md text-sm font-semibold uppercase tracking-wide whitespace-nowrap transition-colors flex items-center justify-between gap-3 ${selectedCategory === cat.slug ? 'bg-athletic-500 text-white' : 'bg-dark-50 text-dark-700 hover:bg-dark-100'}`}
                    >
                      <span>{cat.name}</span> <span className="text-xs opacity-80">{cat.productCount}</span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Content */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-3 mb-6 pb-4 border-b border-dark-100">
                <p className="flex items-center gap-2 text-sm text-dark-600">
                  <LayoutGrid className="w-4 h-4 text-athletic-600" />
                  <span className="font-semibold text-dark-900">{filteredProducts.length}</span> product{filteredProducts.length !== 1 ? 's' : ''}
                </p>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-dark-300 text-dark-700 text-sm rounded-md px-4 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-athletic-500 cursor-pointer"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name: A-Z</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 pointer-events-none" />
                </div>
              </div>

              {/* Grid */}
              {filteredProducts.length === 0 ? (
                <div className="bg-dark-50 rounded-md p-12 text-center border border-dark-100">
                  <p className="text-dark-600">No products found in this category.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
